import { z } from 'zod';

/** Rollen (fest, Seed) */
export const ROLE_KEYS = ['admin', 'vorstand', 'forst', 'flaeche', 'projekt'] as const;
export type RoleKey = (typeof ROLE_KEYS)[number];
export const ROLE_NAMES: Record<RoleKey, string> = {
  admin: 'Administration',
  vorstand: 'Vorstand',
  forst: 'Forstfachliche Leitung',
  flaeche: 'Flächenmanagement',
  projekt: 'Projektmanagement',
};

/** Module der Rechte-Matrix */
export const MODULES = [
  'dashboard',
  'contacts',
  'areas',
  'actions',
  'checklists',
  'monitoring',
  'documents',
  'emails',
  'templates',
  'settings',
  'users',
  'import',
  'audit',
  'export',
] as const;
export type ModuleKey = (typeof MODULES)[number];
export const MODULE_NAMES: Record<ModuleKey, string> = {
  dashboard: 'Übersicht',
  contacts: 'Kontakte',
  areas: 'Flächen',
  actions: 'Aktionen',
  checklists: 'Checklisten',
  monitoring: 'Monitoring',
  documents: 'Dokumente',
  emails: 'E-Mails',
  templates: 'Vorlagen',
  settings: 'Einstellungen',
  users: 'Nutzer',
  import: 'Import',
  audit: 'Änderungsprotokoll',
  export: 'Export',
};

export const PERMISSION_LEVELS = ['none', 'read', 'write'] as const;
export type PermissionLevel = (typeof PERMISSION_LEVELS)[number];
export const levelRank: Record<PermissionLevel, number> = { none: 0, read: 1, write: 2 };

export type PermissionMatrix = Record<RoleKey, Record<ModuleKey, PermissionLevel>>;

const row = (o: Partial<Record<ModuleKey, PermissionLevel>>): Record<ModuleKey, PermissionLevel> =>
  Object.fromEntries(MODULES.map((m) => [m, o[m] ?? 'none'])) as Record<ModuleKey, PermissionLevel>;

/** Standardmatrix laut Plan 01 Abschnitt 3.1 */
export const DEFAULT_PERMISSIONS: PermissionMatrix = {
  admin: row(Object.fromEntries(MODULES.map((m) => [m, 'write'])) as Record<ModuleKey, PermissionLevel>),
  vorstand: row({
    dashboard: 'read', contacts: 'read', areas: 'read', actions: 'read', checklists: 'read', monitoring: 'read',
    documents: 'read', audit: 'read', export: 'read',
  }),
  forst: row({
    dashboard: 'write', contacts: 'write', areas: 'write', actions: 'write', checklists: 'write', monitoring: 'write',
    documents: 'write', emails: 'write', templates: 'write', settings: 'read', export: 'read',
  }),
  flaeche: row({
    dashboard: 'write', contacts: 'read', areas: 'write', actions: 'read', checklists: 'write', monitoring: 'write',
    documents: 'write', emails: 'write', export: 'read',
  }),
  projekt: row({
    dashboard: 'write', contacts: 'write', areas: 'read', actions: 'write', checklists: 'write', monitoring: 'read',
    documents: 'write', emails: 'write', export: 'read',
  }),
};

export function effectivePermissions(
  roles: RoleKey[],
  matrix: PermissionMatrix,
): Record<ModuleKey, PermissionLevel> {
  const out = row({});
  for (const r of roles) {
    const m = matrix[r];
    if (!m) continue;
    for (const k of MODULES) {
      if (levelRank[m[k] ?? 'none'] > levelRank[out[k]]) out[k] = m[k];
    }
  }
  return out;
}

export function can(
  perms: Record<ModuleKey, PermissionLevel> | undefined,
  module: ModuleKey,
  level: Exclude<PermissionLevel, 'none'>,
): boolean {
  if (!perms) return false;
  return levelRank[perms[module] ?? 'none'] >= levelRank[level];
}

/** Geteilte Wertetypen (Plan 01 Abschnitt 3.6) */
export const ReminderSchema = z.object({ date: z.string().nullable(), note: z.string().default('') });
export type Reminder = z.infer<typeof ReminderSchema>;
export const DateListSchema = z.array(z.string());
export type DateList = z.infer<typeof DateListSchema>;
export const ChecksWithDateSchema = z.record(z.union([z.string(), z.literal(true)]));
export type ChecksWithDate = z.infer<typeof ChecksWithDateSchema>;
export const RatingGradeSchema = z.enum(['A', 'B', 'C']);
export type RatingGrade = z.infer<typeof RatingGradeSchema>;
export const RatingSchema = z.object({
  grade: RatingGradeSchema,
  reason: z.string(),
  rated_at: z.string(),
  rated_by: z.string().nullable(),
});
export type Rating = z.infer<typeof RatingSchema>;

export interface ListResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  facets?: Record<string, { value: string; count: number }[]>;
}

export interface ApiError {
  error: { code: string; message: string; fields?: Record<string, string> };
}

export interface UserDto {
  id: number;
  email: string;
  display_name: string;
  initials: string | null;
  is_active: boolean;
  daily_digest: boolean;
  last_login_at: string | null;
  is_local: boolean;
  roles: RoleKey[];
  /** E-Mail-Signatur (Plan 06) */
  signature?: string | null;
}

export interface MeResponse {
  user: UserDto;
  roles: RoleKey[];
  permissions: Record<ModuleKey, PermissionLevel>;
  settings: Record<string, unknown>;
  /** Stand des Mail.Send-Consents (Plan 06) */
  graph?: { configured: boolean; consented: boolean; consented_at: string | null; consent_url: string | null };
}

export interface AuthInfo {
  entra: boolean;
  local: boolean;
}

export const DOCUMENT_ENTITY_TYPES = [
  'contact',
  'area',
  'site_visit',
  'action',
  'checklist_item',
  'monitoring',
  'email',
  'template',
  'lookup',
] as const;
export type DocumentEntityType = (typeof DOCUMENT_ENTITY_TYPES)[number];

export interface DocumentDto {
  id: number;
  entity_type: DocumentEntityType;
  entity_id: number;
  folder: string;
  kind: string;
  original_name: string;
  mime: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  has_thumb: boolean;
  external_url: string | null;
  title: string | null;
  note: string | null;
  taken_at: string | null;
  created_at: string;
  created_by: number | null;
  created_by_name?: string | null;
  archived_at: string | null;
}

export interface DueItem {
  key: string;
  kind: string;
  title: string;
  href: string;
  due: string | null;
  late: boolean;
  forUser: boolean;
}
export interface TodayKpi {
  label: string;
  value: number | string;
  detail: string;
  variant: 'ok' | 'alert' | 'water' | 'plain';
  href?: string;
}
export interface TodayResponse {
  date: string;
  items: DueItem[];
  kpis: TodayKpi[];
}

export interface SearchHit {
  type: 'contact' | 'area' | 'action';
  id: number;
  title: string;
  sub: string;
  href: string;
}
export interface SearchResponse {
  contacts: SearchHit[];
  areas: SearchHit[];
  actions: SearchHit[];
}

export interface AuditEntryDto {
  id: number;
  at: string;
  user_id: number | null;
  user_name: string | null;
  action: string;
  entity_type: string;
  entity_id: number | null;
  changes: Record<string, unknown> | null;
}
